import { useMemo, useState } from "react";
import { useData } from "@/lib/store";
import { useSearchParams } from "react-router-dom";
import { TYPE_META, PRIORITY_META, STATUS_LABELS, formatDue, cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HEADERS = [
  { key: "title", label: "Item" },
  { key: "type", label: "Type" },
  { key: "status", label: "Status" },
  { key: "space_id", label: "Space" },
  { key: "due_date", label: "Due" },
  { key: "priority", label: "Priority" },
];

export default function GridPage() {
  const { items, spaces, updateItem } = useData();
  const [params] = useSearchParams();
  const spaceFilter = params.get("space");
  const [sortKey, setSortKey] = useState("due_date");
  const [sortDir, setSortDir] = useState("asc");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const rows = useMemo(() => {
    let r = items.slice();
    if (spaceFilter) r = r.filter((i) => i.space_id === spaceFilter);
    if (typeFilter !== "all") r = r.filter((i) => i.type === typeFilter);
    if (statusFilter !== "all") r = r.filter((i) => i.status === statusFilter);
    r.sort((a, b) => {
      const va = a[sortKey] || ""; const vb = b[sortKey] || "";
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
    return r;
  }, [items, spaceFilter, sortKey, sortDir, typeFilter, statusFilter]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  return (
    <div className="animate-fade-in-up">
      <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
        <div className="flex flex-wrap gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="grid-type-filter" className="w-36 rounded-full bg-surface-container border-none text-sm h-9"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent className="rounded-2xl bg-surface">
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="grid-status-filter" className="w-36 rounded-full bg-surface-container border-none text-sm h-9"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="rounded-2xl bg-surface">
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-hero bg-surface-container overflow-hidden" data-testid="grid-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-surface-onVariant">
              {HEADERS.map((h) => (
                <th key={h.key} className="px-4 py-3 font-medium">
                  <button onClick={() => toggleSort(h.key)} className="inline-flex items-center gap-1 hover:text-primary transition-colors duration-200 ease-md">
                    {h.label}
                    {sortKey === h.key && <ChevronDown className={cn("w-3 h-3 transition-transform duration-200", sortDir === "desc" && "rotate-180")} />}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((it) => {
              const space = spaces.find((s) => s.id === it.space_id);
              const type = TYPE_META[it.type];
              const prio = PRIORITY_META[it.priority];
              return (
                <tr key={it.id} data-testid={`grid-row-${it.id}`} className="border-t border-outline-variant hover:bg-primary/5 transition-colors duration-200 ease-md">
                  <td className="px-4 py-3 max-w-md">
                    <div className={cn("text-surface-on font-medium truncate", it.status === "done" && "line-through opacity-60")}>{it.title}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full", type.color)}>{type.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={it.status} onValueChange={(v) => updateItem(it.id, { status: v })}>
                      <SelectTrigger className="w-32 h-8 rounded-full bg-surface border-outline-variant text-xs" data-testid={`grid-status-${it.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-2xl bg-surface">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {space && <span className="inline-flex items-center gap-1.5 text-xs text-surface-onVariant"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: space.color }} />{space.name}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-surface-onVariant">{it.due_date ? formatDue(it.due_date) : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full", prio.color)}>{prio.label}</span>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-sm text-surface-onVariant">No items match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
